import type {
  SearchResponse,
  MainpageResponse,
  PicspageResponse,
  VfpageResponse,
  AtrpageResponse,
  IntpageResponse,
  VehicleSelectorResponse,
  FilterOptions,
} from "./types";
import { DELIMITER, API_BASE_URL } from "./constants";

const TIMEOUT_MS = 30000;

// These must ALWAYS be sent so the API returns sidebar filter data
const ALL_ENABLED_FILTERS = [
  "availability",
  "brand_name",
  "stores",
  "cat_name",
  "subcat_name",
  "part_name",
  "vehicle_key",
];
const ALL_ENABLED_RANGES = [
  "ratings_ranges",
  "reviews_ranges",
  "price_ranges",
];

// ─── URL builders ────────────────────────────────────────────────

/**
 * Build URL for client-side (browser) fetches.
 * Goes direct to the backend API.
 */
function clientUrl(endpoint: string, params: URLSearchParams): string {
  return `${API_BASE_URL}/${endpoint}?${params.toString()}`;
}

// ─── Helpers ─────────────────────────────────────────────────────

function ensureEnabledParams(apiParams: URLSearchParams): void {
  const existingFilters = apiParams.get("enabled_filters");
  const existingSet = new Set(existingFilters ? existingFilters.split(DELIMITER) : []);
  for (const f of ALL_ENABLED_FILTERS) existingSet.add(f);
  apiParams.set("enabled_filters", Array.from(existingSet).join(DELIMITER));

  const existingRanges = apiParams.get("enabled_ranges");
  const rangeSet = new Set(existingRanges ? existingRanges.split(DELIMITER) : []);
  for (const r of ALL_ENABLED_RANGES) rangeSet.add(r);
  apiParams.set("enabled_ranges", Array.from(rangeSet).join(DELIMITER));
}

async function fetchWithTimeout(
  url: string,
  options?: RequestInit & { next?: { revalidate?: number } }
): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    return res;
  } finally {
    clearTimeout(id);
  }
}

function prepareSearchParams(
  params: URLSearchParams,
  from: number = 0
): URLSearchParams {
  const apiParams = new URLSearchParams(params.toString());
  if (!apiParams.has("hitsSize")) apiParams.set("hitsSize", "20");
  if (!apiParams.has("from")) apiParams.set("from", String(from));
  apiParams.delete("FilterNav");
  apiParams.delete("expanded");
  ensureEnabledParams(apiParams);
  return apiParams;
}

function pg3Params(reqType: string, idCodes: string, extra?: Record<string, string>): URLSearchParams {
  const p = new URLSearchParams();
  p.set("req_type", reqType);
  p.set("id_codes", idCodes);
  if (extra) {
    for (const [k, v] of Object.entries(extra)) p.set(k, v);
  }
  return p;
}

async function fetchPg3<T>(url: string, options?: Parameters<typeof fetchWithTimeout>[1]): Promise<T | null> {
  const res = await fetchWithTimeout(url, options);
  const data = await res.json();
  const item = Array.isArray(data) ? data[0] : data;
  return item || null;
}

// ─── Search (search_pg_one) ──────────────────────────────────────

/** Client-side search — used for "Load More" in ProductList */
export async function searchProducts(
  params: URLSearchParams,
  from: number = 0
): Promise<SearchResponse> {
  const apiParams = prepareSearchParams(params, from);
  const url = clientUrl("search_pg_one", apiParams);
  const res = await fetchWithTimeout(url);
  return res.json();
}

// ─── Product Detail (search_pg_three) ────────────────────────────

// -- Store tab --
export async function getMainpage(idCodes: string): Promise<MainpageResponse | null> {
  const p = pg3Params("mainpage", idCodes, { dummy: "" });
  const url = clientUrl("search_pg_three", p);
  const item = await fetchPg3<MainpageResponse>(url);
  if (!item?.groupIndex?.length) return null;
  return item;
}

// -- Pics tab --
export async function getPicspage(idCodes: string): Promise<PicspageResponse | null> {
  const p = pg3Params("picspage", idCodes, { dummy: "" });
  return fetchPg3<PicspageResponse>(clientUrl("search_pg_three", p));
}

// -- Fits tab --
export async function getVfpage(
  idCodes: string, from = 0, hitsSize = 20, sort = "", desc = "no",
  filters: Record<string, string> = {}
): Promise<VfpageResponse | null> {
  const extra: Record<string, string> = {
    hitsSize: String(hitsSize), from: String(from), sort, desc,
  };
  // Each non-empty filter becomes an exact-match column param, which the backend
  // turns into `AND "<column>" = <value>`. Filtering is server-side, so total and
  // pagination stay correct across the full result set (not just loaded rows).
  for (const [col, val] of Object.entries(filters)) {
    if (val !== "") extra[col] = val;
  }
  const p = pg3Params("vfpage", idCodes, extra);
  return fetchPg3<VfpageResponse>(clientUrl("search_pg_three", p));
}

// -- Specs tab --
export async function getAtrpage(idCodes: string): Promise<AtrpageResponse | null> {
  const p = pg3Params("atrpage", idCodes, { dummy: "" });
  return fetchPg3<AtrpageResponse>(clientUrl("search_pg_three", p));
}

// -- X-Ref tab --
export async function getIntpage(idCodes: string): Promise<IntpageResponse | null> {
  const p = pg3Params("intpage", idCodes, { dummy: "no" });
  return fetchPg3<IntpageResponse>(clientUrl("search_pg_three", p));
}

// ─── Vehicle Selector ────────────────────────────────────────────

export async function getVehicleOptions(
  year?: string | number, make?: string, model?: string
): Promise<VehicleSelectorResponse> {
  const params = new URLSearchParams();
  if (year) params.set("year", String(year));
  if (make) params.set("make", make);
  if (model) params.set("model", model);
  const url = clientUrl("vehicle_selector", params);
  const res = await fetchWithTimeout(url);
  return res.json();
}

// ─── Search Completion (autocomplete) ────────────────────────────

export async function searchCompletion(search: string): Promise<string[]> {
  const params = new URLSearchParams();
  params.set("search", search);
  const url = clientUrl("search_completion", params);
  const res = await fetchWithTimeout(url);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

// ─── Filter Options Extractor ────────────────────────────────────

export function extractFilterOptions(data: SearchResponse): FilterOptions {
  const brands: FilterOptions["brands"] = [];
  const stores: FilterOptions["stores"] = [];
  const availability: FilterOptions["availability"] = [];
  const ratings: FilterOptions["ratings"] = [];
  const reviews: FilterOptions["reviews"] = [];
  const prices: FilterOptions["prices"] = [];
  const catName: FilterOptions["categories"]["cat_name"] = [];
  const subcatName: FilterOptions["categories"]["subcat_name"] = [];
  const partName: FilterOptions["categories"]["part_name"] = [];

  const attrBucket = data["attributes bucket"];
  if (Array.isArray(attrBucket)) {
    for (const item of attrBucket) {
      if (item.parent_filter === "brand_name") {
        brands.push({ label: item.label, doc_count: item.doc_count });
      } else if (item.parent_filter === "stores") {
        stores.push({ label: item.label, doc_count: item.doc_count });
      } else if (item.parent_filter === "availability") {
        availability.push({ label: item.label, doc_count: item.doc_count });
      }
    }
  }

  const catBuckets = data["categories buckets"];
  if (Array.isArray(catBuckets)) {
    for (const item of catBuckets) {
      if (item.parent_filter === "cat_name") {
        catName.push({ key: item.key, doc_count: item.doc_count });
      } else if (item.parent_filter === "subcat_name") {
        subcatName.push({ key: item.key, doc_count: item.doc_count });
      } else if (item.parent_filter === "part_name") {
        partName.push({ key: item.key, doc_count: item.doc_count });
      }
    }
  }

  const rangesArr = data.ranges;
  if (Array.isArray(rangesArr) && rangesArr.length > 0) {
    const rangesObj = rangesArr[0];
    if (rangesObj.ratings_ranges) ratings.push(...rangesObj.ratings_ranges);
    if (rangesObj.reviews_ranges) reviews.push(...rangesObj.reviews_ranges);
    if (rangesObj.price_ranges) prices.push(...rangesObj.price_ranges);
  }

  const SKIP_PARENTS = new Set(["brand_name", "stores", "availability", "cat_name", "subcat_name", "part_name"]);
  const attributeGroups: FilterOptions["attributeGroups"] = [];
  if (Array.isArray(data.filters)) {
    for (const f of data.filters) {
      if (f.grand_parent === "attributes" && !SKIP_PARENTS.has(f.parent) && f.doc_count > 0) {
        attributeGroups.push({ parent: f.parent, doc_count: f.doc_count });
      }
    }
  }

  return {
    brands,
    stores,
    availability,
    categories: { cat_name: catName, subcat_name: subcatName, part_name: partName },
    ratings,
    reviews,
    prices,
    attributes: data["attributes bucket"] || [],
    attributeGroups,
  };
}
